import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";

import { 
  ArrowRight, Calendar, Users, Heart, 
  Lightbulb, Building, HandHeart, Youtube, Twitch, Globe, 
  MapPin, Car, Accessibility, Baby, Clock, Check, X,
  FileText, MessageSquare, BarChart3, Handshake, Phone, Sparkles,
  GraduationCap, Utensils
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Helmet } from "react-helmet-async";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const Home: React.FC = () => {
  const [aiPrompt, setAiPrompt] = useState("");
  const { organization } = useOrganization();
  const location = useLocation();
  
  const [heroVideo, setHeroVideo] = useState({
    url: "https://youtu.be/CpjGwzS31wg",
    autoplay: true,
    muted: true,
    loop: true,
    controls: false
  });
  const [featuredEvent, setFeaturedEvent] = useState<{
    name: string;
    cause: string;
    location: string;
    event_date: string;
    doors_time: string;
    event_time: string;
    description: string | null;
  } | null>(null);

  useEffect(() => {
    loadSettings();
    loadFeaturedEvent();
  }, []);

  const loadSettings = async () => {
    let query = supabase
      .from('settings')
      .select('key, value')
      .in('key', [
        'HERO_VIDEO_URL',
        'HERO_VIDEO_AUTOPLAY',
        'HERO_VIDEO_MUTED',
        'HERO_VIDEO_LOOP',
        'HERO_VIDEO_CONTROLS'
      ]);
    
    if (organization?.id) {
      query = query.eq('organization_id', organization.id);
    }

    const { data, error } = await query;

    if (data && !error) {
      const settings: Record<string, string> = {};
      data.forEach(item => settings[item.key] = item.value);
      
      if (settings.HERO_VIDEO_URL) {
        setHeroVideo({
          url: settings.HERO_VIDEO_URL,
          autoplay: settings.HERO_VIDEO_AUTOPLAY === 'true',
          muted: settings.HERO_VIDEO_MUTED === 'true',
          loop: settings.HERO_VIDEO_LOOP === 'true',
          controls: settings.HERO_VIDEO_CONTROLS === 'true'
        });
      }
    }
  };

  const loadFeaturedEvent = async () => {
    let query = supabase
      .from('events')
      .select('*')
      .eq('is_featured', true);
    
    if (organization?.id) {
      query = query.eq('organization_id', organization.id);
    }

    const { data, error } = await query.maybeSingle();

    if (data && !error) {
      setFeaturedEvent(data);
    }
  };

  const handleHeroAiSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (aiPrompt.trim()) {
      window.dispatchEvent(new CustomEvent("openChatPaneWithMessage", { 
        detail: { message: aiPrompt } 
      }));
      setAiPrompt("");
    }
  };

  const getVideoEmbedUrl = () => {
    const url = heroVideo.url;
    let videoId = '';
    if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0] || '';
    } else if (url.includes('youtube.com/watch')) {
      const urlParams = new URLSearchParams(url.split('?')[1]);
      videoId = urlParams.get('v') || '';
    } else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('embed/')[1]?.split('?')[0] || '';
    }
    
    const params = new URLSearchParams();
    if (heroVideo.autoplay) params.set('autoplay', '1');
    if (heroVideo.muted) params.set('mute', '1');
    if (heroVideo.loop) {
      params.set('loop', '1');
      params.set('playlist', videoId);
    }
    params.set('controls', heroVideo.controls ? '1' : '0');
    params.set('showinfo', '0');
    params.set('rel', '0');
    params.set('modestbranding', '1');
    
    return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
  };

  const formatEventDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const faqs = [
    { q: "Do I need AI experience?", a: "No experience required! Our training tables and mentors help everyone learn, from complete beginners to experienced builders." },
    { q: "Can kids attend?", a: "Yes! We have an indoor play area staffed by licensed childcare providers. You can indicate interest in kids' AI activities during registration." },
    { q: "What's the schedule?", a: "Doors open at 11am with networking. The build event runs from 12pm to 4pm, including team intros, live sessions, networking, lightning talks, and team showcases." },
    { q: "How do teams work?", a: "Teams work on real nonprofit challenges with coaching. You can join a team or register individually and we'll help match you." },
    { q: "What should I bring?", a: "Just bring your laptop and enthusiasm. We provide food, tools, and learning materials." },
    { q: "Will food be provided?", a: "Yes, meals and refreshments will be provided throughout the day." }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": { "@type": "Answer", "text": faq.a }
    }))
  };

  const nonprofitSolutions = [
    { icon: FileText, title: "Intake & Triage Tools", desc: "Streamline client intake and prioritize cases with AI-powered workflows" },
    { icon: HandHeart, title: "Donor & Volunteer Management", desc: "Automate outreach and track engagement more effectively" },
    { icon: MessageSquare, title: "Communication Assistants", desc: "Draft emails, social posts, and newsletters in seconds" },
    { icon: BarChart3, title: "Impact Reporting", desc: "Generate reports and visualizations for funders and boards" },
    { icon: Handshake, title: "Resource Matching", desc: "Connect clients to the right services and resources faster" },
    { icon: Phone, title: "Hotline Support", desc: "AI-assisted call routing and follow-up tracking" },
    { icon: Sparkles, title: "Whatever Your Mission Needs", desc: "Custom solutions built around your unique challenges and goals" }
  ];

  const eventExperienceSteps = [
    { step: 1, title: "Welcome & Story Setup", desc: "Introduction to the causes and nonprofits we're serving" },
    { step: 2, title: "Team Building & Kickoff", desc: "Teams form and define their project goals" },
    { step: 3, title: "Live Building + Training", desc: "Hands-on building with mentor support and training tables" },
    { step: 4, title: "Networking & Connections", desc: "Built-in breaks to meet peers and potential employers" },
    { step: 5, title: "Lightning Talks", desc: "Quick, high-impact talks from experienced AI builders" },
    { step: 6, title: "Showcase & Celebration", desc: "Teams present solutions and celebrate together" }
  ];

  const worksOfMercy = {
    corporal: [
      "Feed the hungry",
      "Give drink to the thirsty",
      "Clothe the naked",
      "Shelter the homeless",
      "Visit the sick",
      "Visit the imprisoned",
      "Bury the dead"
    ],
    spiritual: [
      "Counsel the doubtful",
      "Instruct the ignorant",
      "Admonish the sinner",
      "Comfort the sorrowful",
      "Forgive offenses",
      "Bear wrongs patiently",
      "Pray for the living and dead"
    ]
  };

  const canonicalUrl = `https://buildwithchrist.ai${location.pathname === '/' ? '' : location.pathname}`;

  return (
    <div className="relative">
      <Helmet>
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Hero Section with Video Background */}
      <section id="top" className="min-h-[90vh] relative flex items-center overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          {heroVideo.url && (
            <iframe
              src={getVideoEmbedUrl()}
              title="Background Video"
              className="absolute inset-0 w-full h-full object-cover scale-150"
              allow="autoplay; encrypted-media"
              frameBorder="0"
              style={{ pointerEvents: 'none' }}
            />
          )}
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-background/80" />
        </div>

        <div className="max-w-7xl mx-auto px-4 py-20 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
            {/* Left Column - Main Content (60%) */}
            <div className="lg:col-span-3 space-y-6">
              <div className="inline-flex items-center gap-2 bg-sky-500/20 border border-sky-400/30 px-4 py-1.5 rounded-full text-sm">
                <Calendar size={14} className="text-sky-300" />
                <span className="text-sky-300 font-medium">
                  Next Event: {featuredEvent ? formatEventDate(featuredEvent.event_date) : 'March 7, 2026'}
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight font-display">
                Learn AI by Serving Others
              </h1>

              <p className="text-lg text-muted-foreground max-w-xl">
                Join a 4-hour build event where teams create working AI solutions 
                for organizations serving those most in need.
              </p>

              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Check size={16} className="text-secondary" />
                  Working prototypes, not just concepts
                </li>
                <li className="flex items-center gap-2">
                  <Check size={16} className="text-secondary" />
                  All skill levels welcome — training tables for beginners
                </li>
                <li className="flex items-center gap-2">
                  <Check size={16} className="text-secondary" />
                  Family-friendly with licensed childcare
                </li>
                <li className="flex items-center gap-2">
                  <Check size={16} className="text-secondary" />
                  Livestreamed for local impact, global reach
                </li>
              </ul>

              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <Button size="lg" className="text-base" asChild>
                  <Link to="/attendees#register-form">
                    Register to Attend
                    <ArrowRight size={18} className="ml-2" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="text-base" asChild>
                  <Link to="/nonprofits">Become a Mission Partner</Link>
                </Button>
              </div>
            </div>

            {/* Right Column - Event Card (40%) */}
            <div className="lg:col-span-2 hidden lg:block">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 space-y-4 shadow-lg">
                <span className="text-sm font-medium text-primary uppercase tracking-wider">Upcoming Event</span>
                
                <h2 className="text-3xl font-bold font-display text-slate-800">
                  {featuredEvent ? formatEventDate(featuredEvent.event_date) : 'March 7, 2026'}
                </h2>
                
                <p className="text-lg text-primary">
                  Building for: {featuredEvent?.cause || 'Victims of Domestic Violence'}
                </p>
                
                <div className="border-t border-slate-200 pt-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <MapPin className="text-primary" size={16} />
                    </div>
                    <span className="text-base text-slate-700">{featuredEvent?.location || 'Phase Events, Alpharetta, GA'}</span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Clock className="text-primary" size={16} />
                    </div>
                    <span className="text-base text-slate-700">
                      Doors {featuredEvent?.doors_time || '11am'} • Build {featuredEvent?.event_time || '12–4pm'}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Baby className="text-primary" size={16} />
                    </div>
                    <span className="text-base text-slate-700">Family-friendly with licensed childcare</span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <GraduationCap className="text-primary" size={16} />
                    </div>
                    <span className="text-base text-slate-700">All skill levels — beginners to experts</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Teaser Band */}
      <section className="py-6 px-4 bg-slate-50 border-y border-slate-200 shadow-sm animate-fade-in">
        <div className="max-w-2xl mx-auto">
          <form onSubmit={handleHeroAiSubmit} className="flex items-center gap-3">
            <Sparkles className="text-primary shrink-0" size={20} />
            <span className="text-slate-600 text-sm whitespace-nowrap hidden sm:inline">
              Have questions? Try our AI assistant
            </span>
            <div className="flex-1 flex bg-white border border-slate-200 rounded-lg overflow-hidden">
              <Input
                type="text"
                placeholder="Ask about the event or show me around the site"
                className="flex-grow border-0 bg-transparent focus-visible:ring-0 text-sm h-10 text-slate-800 placeholder:text-slate-400"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
              />
              <Button type="submit" size="icon" variant="ghost" className="h-10 w-10 shrink-0 text-primary hover:text-primary/80" aria-label="Submit question">
                <ArrowRight size={16} />
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* A Different Approach */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm font-medium text-primary uppercase tracking-wider mb-2 block">A Different Approach</span>
            <h2 className="text-3xl md:text-4xl font-bold font-display mb-4 text-slate-800">
              Nonprofits need talent and resources. Most can't afford them.
            </h2>
            <p className="text-xl text-primary font-medium">We bridge the gap—for free.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* The Typical Path */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 md:p-8">
              <h3 className="text-xl font-semibold mb-6 text-slate-500 font-display">The Typical Path</h3>
              <ul className="space-y-4">
                {[
                  "Staff stretched thin, wearing too many hats",
                  "Volunteers with good intentions but limited technical training",
                  "Grants that fund programs but not operations or technology",
                  "No time to learn new skills while keeping the mission running",
                  "Technology that's outdated or too complex to maintain"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-slate-600">
                    <X size={18} className="text-destructive shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* The BuildWithChrist Way */}
            <div className="bg-primary/5 border border-primary/30 rounded-xl p-6 md:p-8">
              <h3 className="text-xl font-semibold mb-6 text-primary font-display">The BuildWithChrist Way</h3>
              <ul className="space-y-4">
                {[
                  "Skilled volunteers who learn AI by solving your real problems",
                  "Working prototypes delivered in 4 hours, completely free",
                  "Hands-on coaching so everyone can learn and grow",
                  "A win-win model: builders gain skills, nonprofits gain solutions"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-slate-700">
                    <Check size={18} className="text-secondary shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What We Deliver */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm font-medium text-primary uppercase tracking-wider mb-2 block">What We Deliver</span>
            <h2 className="text-3xl md:text-4xl font-bold font-display mb-4">
              Example Problems Well-Suited for AI Solutions
            </h2>
          </div>

          {/* For Nonprofits - Prominent Cards */}
          <div className="mb-16">
            <h3 className="text-2xl font-semibold font-display mb-6 flex items-center gap-2">
              <Building className="text-primary" size={24} />
              For Nonprofits
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {nonprofitSolutions.slice(0, 6).map((solution, idx) => (
                <div 
                  key={idx} 
                  className="bg-card border border-border rounded-xl p-5 hover:border-primary/50 transition-colors"
                >
                  <solution.icon className="text-primary mb-3" size={24} />
                  <h4 className="font-semibold mb-2">{solution.title}</h4>
                  <p className="text-sm text-muted-foreground">{solution.desc}</p>
                </div>
              ))}
            </div>
            {/* Centered "Whatever Your Mission Needs" card */}
            <div className="flex justify-center mt-4">
              <div className="bg-card border border-primary/30 bg-primary/5 rounded-xl p-5 hover:border-primary/50 transition-colors max-w-md w-full">
                <Sparkles className="text-primary mb-3" size={24} />
                <h4 className="font-semibold mb-2">{nonprofitSolutions[6].title}</h4>
                <p className="text-sm text-muted-foreground">{nonprofitSolutions[6].desc}</p>
              </div>
            </div>
            <div className="mt-6 text-center">
              <Button variant="outline" asChild>
                <Link to="/nonprofits">Become a Mission Partner</Link>
              </Button>
            </div>
          </div>

        </div>
      </section>

      {/* How It Works - Timeline with Circles */}
      <section id="how-it-works" className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-primary uppercase tracking-wider mb-2 block">How It Works</span>
            <h2 className="text-3xl md:text-4xl font-bold font-display mb-4 text-slate-800">From problem to prototype in 4 hours</h2>
          </div>

          {/* Timeline with numbered circles */}
          <div className="relative">
            {/* Connecting line - desktop */}
            <div className="hidden md:block absolute top-6 left-[calc(12.5%+24px)] right-[calc(12.5%+24px)] h-0.5 bg-primary/20" />
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-4">
              {[
                { step: "01", title: "Hear the Story", desc: "We introduce the nonprofits and share the real challenges they face serving their communities." },
                { step: "02", title: "Team Introductions", desc: "Teams form around the challenges that inspire them, mixing skills and experience levels." },
                { step: "03", title: "Build Together", desc: "4 hours of focused building with mentors, training tables, and live support." },
                { step: "04", title: "Deliver & Celebrate", desc: "Teams present their solutions. We celebrate creativity, usefulness, and service." }
              ].map((item, idx) => (
                <div key={idx} className="relative text-center">
                  {/* Mobile connecting line */}
                  {idx < 3 && (
                    <div className="md:hidden absolute left-1/2 top-12 w-0.5 h-8 bg-primary/20 -translate-x-1/2" />
                  )}
                  
                  {/* Numbered circle */}
                  <div className="w-12 h-12 mx-auto rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm relative z-10 shadow-lg">
                    {item.step}
                  </div>
                  
                  <h3 className="font-semibold mt-4 mb-2 text-slate-800">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-12">
            <Button asChild>
              <Link to="/how-it-works">Learn More About the Event</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* The Event Experience - Moved here to follow How It Works */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm font-medium text-primary uppercase tracking-wider mb-2 block">The Event Experience</span>
            <h2 className="text-3xl md:text-4xl font-bold font-display mb-4 text-slate-800">What Your Day Looks Like</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              From welcome to celebration, here's how the day unfolds.
            </p>
          </div>

          {/* 6 styled cards grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {eventExperienceSteps.map((item, idx) => (
              <div 
                key={idx} 
                className="bg-slate-50 border border-slate-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm shadow-md">
                    {item.step}
                  </div>
                  <h3 className="font-semibold text-slate-700">{item.title}</h3>
                </div>
                <p className="text-sm text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Callout boxes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-secondary/10 border border-secondary/20 rounded-xl p-6 flex items-start gap-4">
              <div className="p-3 bg-secondary/20 rounded-full shrink-0">
                <Baby className="text-secondary" size={24} />
              </div>
              <div>
                <h4 className="font-semibold mb-1 text-slate-800">Family-Friendly</h4>
                <p className="text-sm text-slate-600">
                  Licensed childcare with indoor play area. Parents can focus on building while kids play safely next door.
                </p>
              </div>
            </div>
            
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 flex items-start gap-4">
              <div className="p-3 bg-primary/10 rounded-full shrink-0">
                <GraduationCap className="text-primary" size={24} />
              </div>
              <div>
                <h4 className="font-semibold mb-1 text-slate-800">All Skill Levels Welcome</h4>
                <p className="text-sm text-slate-600">
                  Training tables for beginners, mentors for guidance. No AI experience required to attend and learn.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Entertain, Educate, Elevate */}
      <section className="py-20 px-4 bg-slate-50 border-y border-slate-200">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm font-medium text-primary uppercase tracking-wider mb-2 block">
              The Format
            </span>
            <h2 className="text-3xl md:text-4xl font-bold font-display mb-4 text-slate-800">
              Entertain. Educate. Elevate.
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              More than a hackathon—a live event and global broadcast designed to inspire action.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary/20 flex items-center justify-center">
                <Sparkles className="text-secondary" size={28} />
              </div>
              <h3 className="font-semibold text-lg mb-2 text-slate-800">Entertain</h3>
              <p className="text-sm text-slate-600">
                Engaging stories, live energy, and a show-style experience that keeps audiences inspired—whether in the room or watching from anywhere in the world.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <GraduationCap className="text-primary" size={28} />
              </div>
              <h3 className="font-semibold text-lg mb-2 text-slate-800">Educate</h3>
              <p className="text-sm text-slate-600">
                Hands-on AI training, lightning talks, and mentorship. Learn by building real solutions—no prior experience required.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/20 flex items-center justify-center">
                <Heart className="text-primary" size={28} />
              </div>
              <h3 className="font-semibold text-lg mb-2 text-slate-800">Elevate</h3>
              <p className="text-sm text-slate-600">
                Empower nonprofits with AI tools while elevating builders' skills and careers. Everyone rises together.
              </p>
            </div>
          </div>

          {/* Live Event + Broadcast Callout */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 md:p-8 flex flex-col md:flex-row items-center gap-6">
            <div className="flex gap-4 shrink-0">
              <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
              </div>
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Globe className="text-primary" size={24} />
              </div>
            </div>
            <div className="text-center md:text-left">
              <h4 className="font-semibold text-lg mb-1 text-slate-800">
                Live Event. Global Broadcast.
              </h4>
              <p className="text-slate-600">
                Every event is produced as a live TV-style broadcast—creating local impact in the room and global visibility for the mission partners and builders watching around the world.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Join the Next Event CTA */}
      <section className="py-20 px-4 bg-primary/10 border-y border-primary/20">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-sm font-medium text-primary uppercase tracking-wider mb-2 block">Join the Next Event</span>
          <h2 className="text-3xl md:text-4xl font-bold font-display mb-4">
            {featuredEvent ? formatEventDate(featuredEvent.event_date) : 'March 7, 2026'} — Building for {featuredEvent?.cause || 'A Local Georgia Partner'}
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            {featuredEvent?.description || 'Join us as we build AI tools for a local Georgia nonprofit partner. Every prototype, every solution, makes a real difference.'}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-4">
            <Button size="lg" asChild>
              <Link to="/attendees#register-form">Register to Attend — It's Free</Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/sponsors">Sponsor This Event</Link>
            </Button>
          </div>
          <div className="mb-12" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-2xl mx-auto">
            <div className="text-center">
              <p className="text-2xl font-bold text-primary">100%</p>
              <p className="text-sm text-muted-foreground">Free for nonprofits</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-secondary">4 hrs</p>
              <p className="text-sm text-muted-foreground">Problem to prototype</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-primary">Global</p>
              <p className="text-sm text-muted-foreground">Livestreamed reach</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement - Left Aligned */}
      <section className="py-20 px-4 bg-slate-800">
        <div className="max-w-4xl mx-auto space-y-8">
          <span className="text-sm font-medium text-primary uppercase tracking-wider block">Our Mission</span>
          
          <blockquote className="text-2xl md:text-3xl font-display italic text-slate-200">
            "Technology should serve <span className="text-primary not-italic font-semibold">human flourishing</span>, not replace human connection."
          </blockquote>
          
          <p className="text-slate-300 max-w-2xl">
            We believe AI is a tool that can multiply the impact of those who serve others. 
            By bringing together builders and nonprofits, we create solutions that empower communities, 
            respect dignity, and extend the reach of mercy.
          </p>

          {/* Values - Simple text blocks without cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-8">
            <div>
              <h3 className="font-bold text-lg mb-2 text-white">Service</h3>
              <p className="text-sm text-slate-400">Using our skills to help those who help others.</p>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-2 text-white">Dignity</h3>
              <p className="text-sm text-slate-400">Building AI that respects and elevates human worth.</p>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-2 text-white">Empowerment</h3>
              <p className="text-sm text-slate-400">Equipping nonprofits with tools to expand their reach.</p>
            </div>
          </div>

          <p className="text-sm text-slate-400 pt-4">
            Brought to you by{" "}
            <a 
              href="https://cityonpurpose.com?utm_source=buildwithchrist&utm_medium=website&utm_campaign=bwc_homepage&utm_content=mission_section" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-primary hover:underline font-semibold"
            >
              City on Purpose
            </a>
          </p>
        </div>
      </section>

      {/* Where We Gather */}
      <section id="where-we-gather" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Location Info */}
            <div className="space-y-6">
              <div>
                <span className="text-sm font-medium text-primary uppercase tracking-wider mb-2 block">Where We Gather</span>
                <h2 className="text-3xl md:text-4xl font-bold font-display">Phase Events, Alpharetta, GA</h2>
              </div>
              <p className="text-muted-foreground">
                Our events are held at Phase Events in Alpharetta, Georgia—a flexible, 
                family-friendly environment with spacious rooms and breakout areas.
              </p>

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <MapPin className="text-primary shrink-0" size={18} />
                  <span><strong>Location:</strong> Phase Events, Alpharetta, GA</span>
                </div>
                <div className="flex items-center gap-3">
                  <Car className="text-primary shrink-0" size={18} />
                  <span><strong>Parking:</strong> Free onsite</span>
                </div>
                <div className="flex items-center gap-3">
                  <Accessibility className="text-primary shrink-0" size={18} />
                  <span><strong>Accessibility:</strong> Wheelchair-accessible</span>
                </div>
                <div className="flex items-center gap-3">
                  <Baby className="text-primary shrink-0" size={18} />
                  <span><strong>Childcare:</strong> Licensed indoor play area</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="text-primary shrink-0" size={18} />
                  <span><strong>Schedule:</strong> Doors at 11am, Build 12-4pm</span>
                </div>
              </div>

              {/* Livestream Callout */}
              <div className="bg-card border border-border rounded-xl p-4 mt-6">
                <div className="flex items-start gap-3">
                  <Globe className="text-primary shrink-0 mt-0.5" size={20} />
                  <div>
                    <p className="font-medium mb-1">Can't make it in person?</p>
                    <p className="text-sm text-muted-foreground mb-3">
                      We'll be livestreaming across YouTube, Twitch, and other platforms. 
                      Join from anywhere in the world.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <a 
                        href="https://youtube.com/@buildwithchrist?utm_source=buildwithchrist&utm_medium=website&utm_campaign=livestream&utm_content=homepage_where" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="inline-flex items-center gap-1.5 text-xs bg-background border border-border px-3 py-1.5 rounded-full hover:border-primary transition-colors"
                      >
                        <Youtube size={14} className="text-red-500" /> YouTube
                      </a>
                      <a 
                        href="https://twitch.tv/buildwithchrist?utm_source=buildwithchrist&utm_medium=website&utm_campaign=livestream&utm_content=homepage_where" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs bg-background border border-border px-3 py-1.5 rounded-full hover:border-primary transition-colors"
                      >
                        <Twitch size={14} className="text-purple-500" /> Twitch
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Venue Image */}
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <img 
                src="/assets/heritage-hall.svg" 
                alt="Heritage Hall at Phase Events" 
                className="w-full h-full object-cover min-h-[300px]"
              />
            </div>
          </div>
        </div>
      </section>


      {/* Our Mission Partners */}
      <section className="py-20 px-4 bg-primary/5 border-y border-primary/10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm font-medium text-primary uppercase tracking-wider mb-2 block">Our Mission Partners</span>
            <h2 className="text-3xl md:text-4xl font-bold font-display mb-4">Building for the Works of Mercy</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              We partner with nonprofits serving those most in need—organizations aligned with the 
              Corporal and Spiritual Works of Mercy.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Corporal Works */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <HandHeart className="text-secondary" size={20} />
                Corporal Works of Mercy
              </h3>
              <ul className="space-y-2">
                {worksOfMercy.corporal.map((work, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check size={14} className="text-secondary shrink-0" />
                    {work}
                  </li>
                ))}
              </ul>
            </div>

            {/* Spiritual Works */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Heart className="text-primary" size={20} />
                Spiritual Works of Mercy
              </h3>
              <ul className="space-y-2">
                {worksOfMercy.spiritual.map((work, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check size={14} className="text-primary shrink-0" />
                    {work}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="text-center">
            <Button size="lg" asChild>
              <Link to="/nonprofits">
                Become a Mission Partner
                <ArrowRight size={18} className="ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section - 2 Column Accordion Grid */}
      <section id="faq" className="py-20 px-4 bg-card/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm font-medium text-primary uppercase tracking-wider mb-2 block">Frequently Asked Questions</span>
            <h2 className="text-3xl md:text-4xl font-bold font-display mb-4">Everything You Need to Know</h2>
            <p className="text-muted-foreground">Common questions about attending our build events</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
            {faqs.map((item, idx) => (
              <Accordion key={idx} type="single" collapsible>
                <AccordionItem value={`faq-${idx}`} className="border-b border-border">
                  <AccordionTrigger className="text-left font-semibold hover:no-underline py-4">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm pb-4">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;